import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOpenAiCompletion } from './useOpenAiCompletion';
import { useOpenAiCompletionSearch } from './useOpenAiCompletionSearch';
import {
  fetchTypeformPayloadByClientIdRequest,
  resetTypeformPayloadState,
} from '@client/model/typeformPayloadModel';
import {
  AppState,
  selectClient,
  selectPreviousFilledApplicationForm,
  selectSession,
  selectSessionAdvisor,
  selectTypeformPayload,
  selectTypeformPayloadError,
  selectTypeformPayloadIsLoading,
  selectCachedFormConfig,
} from '@client';
import {
  enrichPrefillData,
  getRelevantCitations,
  logError,
  PREFILL_COMPLETION_STATUS,
  PREFILL_FORM_TYPE,
  policyFormDefs,
  SessionType,
  filterUncitedPrefills,
} from '@shared';

import {
  populatePrefillData,
  resetPrefillDataState,
} from '@client/model/policyApplicationPrefillModel';
import { useAdvisorBGA } from './useAdvisorBGA';

export function usePolicyApplicationPrefillForm({
  clientId,
  clientPolicyApplicationId,
  formId,
  policyFormType,
  partnerClientId,
  fundingPolicyType,
  policyFundingSource,
}: PrefillParams): UsePolicyApplicationPrefillResult {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<CompletionStatus>(PREFILL_COMPLETION_STATUS.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [primaryClientData, setPrimaryClientData] = useState<ParsedData | null>(null);
  const [primaryClientCitations, setPrimaryClientCitations] = useState<
    PrefillCitations[] | undefined
  >(undefined);

  const typeformPayload = useSelector(selectTypeformPayload);
  const isTypeformLoading = useSelector(selectTypeformPayloadIsLoading);
  const typeformError = useSelector(selectTypeformPayloadError);
  const advisor = useSelector(selectSessionAdvisor);
  const client = useSelector(selectClient);
  
  const formConfig = useSelector((state: AppState) => 
    formId ? selectCachedFormConfig(state, formId) : undefined
  );

  const { startSearch } = useOpenAiCompletionSearch();

  const { createCompletion, reset } = useOpenAiCompletion({
    maxPollingAttempts: 100,
    onSuccess: result => {
      onSuccessfullCompletion(result);
    },
    onError: error => {
      resetOptions('AI completion failed');
      dispatch(resetTypeformPayloadState());
    },
  });

  const { associatedBGA } = useAdvisorBGA({ policyType: fundingPolicyType });

  const advisorInfo = useMemo(
    () => ({ ...advisor, bgaWritingCode: associatedBGA?.carrierMeta?.writingCode || '' }),
    [advisor, associatedBGA],
  );

  useEffect(() => {
    if (isTypeformLoading) {
      return;
    }
    if (typeformError) {
      resetOptions('Cannot fetch intake for the client');
      return;
    }

    if (typeformPayload) {
      callOpenAIforPrefill();
    }
  }, [isTypeformLoading, typeformError, typeformPayload, formId]);

  useEffect(() => {
    resetState();
  }, [clientId, formId, clientPolicyApplicationId, policyFormType]);

  const fetchPrefillData = useCallback(() => {
    if (!clientId || !clientPolicyApplicationId || !formId || !policyFormType) {
      setError('Key parameters missing');
      return;
    }
    resetState();

    setIsLoading(true);
    setError(null);
    setStatus(PREFILL_COMPLETION_STATUS.PROCESSING);

    dispatch(fetchTypeformPayloadByClientIdRequest({ clientId }));
  }, [clientId, clientPolicyApplicationId, formId, policyFormType]);

  const callOpenAIforPrefill = () => {
    const currentClientId =
      status === PREFILL_COMPLETION_STATUS.PROCESSING_PARTNER ? partnerClientId : clientId;

    const aiPromptType =
      policyFormType === PREFILL_FORM_TYPE.UNDERWRITING
        ? 'underwriting-prefill'
        : status === PREFILL_COMPLETION_STATUS.PROCESSING_PARTNER
          ? 'joint-application-prefill'
          : 'application-prefill';

    // Search for existing completion data using callbacks
    startSearch(
      { requesterId: currentClientId, aiPromptType, formId },
      {
        onSuccess: data => {
          onSuccessfullCompletion(data);
        },
        onError: () => {
          createNewCompletion(currentClientId!, aiPromptType);
        },
      },
    );
  };

  const createNewCompletion = (currentClientId: string, aiPromptType: string) => {
    const requestId = createCompletion('', {
      model: 'o3',
      aiPromptType: aiPromptType,
      clientId: currentClientId,
      metadata: {
        typeformPayload: typeformPayload?.typeformPayload || '',
        formId,
        fundingPolicyType,
        primaryClientData,
      },
    });
    if (!requestId) {
      resetOptions('Failed to create AI request');
    }
  };

  const onSuccessfullCompletion = (result: OpenAiCompletionDbRecord) => {
    const parsedData = parseAnswers(result.results || '');
    if (!parsedData) {
      resetOptions('Failed to parse prefill data');
      return;
    }

    if (status === PREFILL_COMPLETION_STATUS.PROCESSING && partnerClientId) {
      setPrimaryClientData(parsedData);
      setPrimaryClientCitations(typeformPayload?.citations);
      setStatus(PREFILL_COMPLETION_STATUS.PROCESSING_PARTNER);
      dispatch(resetTypeformPayloadState());
      dispatch(fetchTypeformPayloadByClientIdRequest({ clientId: partnerClientId }));
    } else if (status === PREFILL_COMPLETION_STATUS.PROCESSING_PARTNER) {
      const mergedAsnwers = [...primaryClientData?.answers!, ...parsedData.answers];
      const enrichedPrefills = enrichPrefillData(
        mergedAsnwers,
        client,
        advisorInfo,
        policyFundingSource!,
        formConfig,
      );
      const citations = getRelevantCitations(
        primaryClientCitations || [],
        enrichedPrefills,
        typeformPayload?.citations || [],
      );
      //Validate prefill data to ensure every intake related answer has a citation
      const validPrefillData = filterUncitedPrefills(enrichedPrefills, citations);
      dispatch(
        populatePrefillData({
          clientId: clientId,
          clientPolicyApplicationId: clientPolicyApplicationId,
          formId: formId,
          prefillData: validPrefillData,
          citations,
          status: 'populated',
          error: null,
        }),
      );
      setStatus(PREFILL_COMPLETION_STATUS.COMPLETED);
      dispatch(resetTypeformPayloadState());
    } else {
      const enrichedPrefills = enrichPrefillData(
        parsedData.answers,
        client,
        advisorInfo,
        policyFundingSource!,
        formConfig,
      );
      const citations = getRelevantCitations(typeformPayload?.citations || [], enrichedPrefills);
      //Validate prefill data to ensure every intake related answer has a citation
      const validPrefillData = filterUncitedPrefills(enrichedPrefills, citations);
      dispatch(
        populatePrefillData({
          clientId: clientId,
          clientPolicyApplicationId: clientPolicyApplicationId,
          formId: formId,
          prefillData: validPrefillData,
          citations,
          status: 'populated',
          error: null,
        }),
      );
      setStatus(PREFILL_COMPLETION_STATUS.COMPLETED);
      dispatch(resetTypeformPayloadState());
    }
  };

  const resetOptions = useCallback((error?: string) => {
    if (error) {
      setError(error);
      setStatus(PREFILL_COMPLETION_STATUS.ERROR);
      logError(
        'usePolicyApplicationPrefillForm',
        'Error in prefill processing',
        { clientId: clientId, formId, policyFormType, fundingPolicyType },
        error,
      );
    } else {
      setError(null);
      setStatus(PREFILL_COMPLETION_STATUS.IDLE);
    }
    setIsLoading(false);
  }, []);

  const resetState = useCallback(() => {
    resetOptions();
    reset();
    dispatch(resetTypeformPayloadState());
    dispatch(resetPrefillDataState());
  }, []);

  return {
    isLoading,
    error,
    status,
    fetchPrefillData,
  };
}

export const usePreviousFilledApplicationForm = (
  clientId: string,
  clientPolicyApplicationId: string,
  formId: string,
) => {
  const { sessionType } = useSelector(selectSession);
  
  // Get form metadata from Redux cache (already fetched by other hooks)
  const formConfig = useSelector((state: AppState) => 
    selectCachedFormConfig(state, formId)
  );
  
  // Determine step type from cached form config or fallback
  const stepType = formConfig?.formMetadata?.formType ?? 'application-form';

  if (sessionType === SessionType.wizard) {
    const prefilledData = useSelector((state: AppState) =>
      selectPreviousFilledApplicationForm(
        state,
        clientId,
        stepType,
        formId,
        clientPolicyApplicationId,
      ),
    );
    return prefilledData;
  } else {
    return [];
  }
};

const parseAnswers = (json: string): ParsedData | null => {
  try {
    return JSON.parse(json) as ParsedData;
  } catch (err) {
    logError(
      'usePolicyApplicationPrefillForm',
      'Failed to parse answers',
      { jsonResponse: json },
      err,
    );
    return null;
  }
};
